import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { SIDEBAR_TREE } from '../../../data/learningZoneMockData';

const ICON_BG = {
  wtf: 'bg-sky-100',
  english: 'bg-red-100',
  'action-songs': 'bg-purple-100',
  scenes: 'bg-rose-100',
  beginners: 'bg-orange-100',
  intermediate: 'bg-blue-100',
  'computer-apps': 'bg-sky-100',
  gcompris: 'bg-indigo-100',
  artweaver: 'bg-pink-100',
  'typing-master': 'bg-sky-100',
  canva: 'bg-blue-100',
  toonytool: 'bg-purple-100',
  'computer-courses': 'bg-yellow-100',
  'life-skills': 'bg-yellow-100',
  stories: 'bg-blue-100',
  'art-therapy': 'bg-pink-100',
  medical: 'bg-red-100',
  homework: 'bg-gray-200',
  diary: 'bg-indigo-100',
};

// Student rows switched off for now (2026-10-27): shown greyed out and not clickable. Their
// destinations are untouched, so taking an id out of this list brings the row back.
const DISABLED_ROWS = new Set(['life-skills', 'homework']);

const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(minutes).padStart(2, '0')} : ${String(secs).padStart(2, '0')}`;
};

/**
 * Elapsed time ("how long the student has been logged in today"), kept in the
 * same localStorage keys TitleBar uses so the count carries across both UIs.
 * Students now land here straight after login, where TitleBar isn't mounted,
 * so this applies the same new-day reset and keeps the counter ticking itself.
 * TitleBar and the Learning Zone are never mounted together, so nothing is
 * counted twice.
 */
function useSessionTime() {
  const [sessionTime, setSessionTime] = useState(() => {
    const today = new Date().toDateString();
    if (localStorage.getItem('sessionResetDate') !== today) {
      localStorage.setItem('sessionResetDate', today);
      localStorage.setItem('sessionTime', '0');
      return 0;
    }
    return parseInt(localStorage.getItem('sessionTime'), 10) || 0;
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setSessionTime((prev) => {
        const next = prev + 1;
        localStorage.setItem('sessionTime', String(next));
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return sessionTime;
}

// lg+: the Learning Zone is a fixed-height shell where the sidebar and the
// main content each scroll on their own, each with a slim scrollbar so it's
// obvious they scroll. Deliberately ::-webkit-scrollbar only: the standard
// `scrollbar-width` / `scrollbar-color` properties are inherited, so setting
// them here leaked into nested scrollers (e.g. the scene tabs strip) and
// forced a scrollbar onto them.
export const LZ_SCROLL_REGION =
  'lg:overflow-y-auto ' +
  '[&::-webkit-scrollbar]:block [&::-webkit-scrollbar]:w-2 ' +
  '[&::-webkit-scrollbar-track]:bg-transparent ' +
  '[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300';

// Path of items from the root down to the row that owns `view` (null if none).
// Children are searched first, so when a parent shares its view with one of its
// own children (English > Scenes) the child is the selected row and the parent
// is just an ancestor.
const findActivePath = (items, view) => {
  for (const item of items) {
    const childPath = item.children ? findActivePath(item.children, view) : null;
    if (childPath) return [item, ...childPath];
    if (item.view === view) return [item];
  }
  return null;
};

// Ids of the groups flagged `defaultOpen`, at any depth.
const collectDefaultOpen = (items) =>
  items.flatMap((item) =>
    item.children ? [...(item.defaultOpen ? [item.id] : []), ...collectDefaultOpen(item.children)] : []
  );

// Left margin (px) per nesting level; the first step is the original 24px child indent.
const indentFor = (depth) => (depth === 0 ? 0 : 16 + depth * 8);

// `iconUrl`: an image uploaded for a category admins added (Courses > Add Category), shown instead of
// the emoji; the emoji comes back if it can't be loaded.
function IconBadge({ id, icon, iconUrl }) {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = Boolean(iconUrl) && !imageFailed;
  return (
    <span
      className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-base ${showImage ? 'overflow-hidden ' : ''}${
        ICON_BG[id] || 'bg-gray-100'
      }`}
    >
      {showImage ? <img src={iconUrl} alt="" className="w-full h-full object-cover" onError={() => setImageFailed(true)} /> : icon}
    </span>
  );
}

// Expand/collapse chevron shared by every group row (▶ turns to ▼ when open).
function ChevronToggle({ label, isOpen, onToggle }) {
  return (
    <button
      onClick={onToggle}
      aria-label={isOpen ? `Collapse ${label}` : `Expand ${label}`}
      aria-expanded={isOpen}
      className="mr-1 p-2 text-xs transition-transform"
      style={{ transform: isOpen ? 'rotate(90deg)' : 'none' }}
    >
      ▶
    </button>
  );
}

// Two row densities. Rows two levels down are compact so long labels
// ("Instrumental Songs") still fit the 256px sidebar.
const ROW_SIZE = {
  regular: { row: 'rounded-xl text-[15px] font-bold', label: 'gap-3 py-2 pl-2', endPad: 'pr-2', idle: 'text-gray-700' },
  compact: { row: 'rounded-lg text-[13px] font-semibold', label: 'gap-1.5 py-1.5 pl-1.5', endPad: 'pr-1.5', idle: 'text-gray-600' },
};

/**
 * One row of the sidebar tree - the same component for every option and
 * sub-option at any depth (it renders its own children). A row is a plain link,
 * or a dropdown when it has `children`: the label selects it and opens the
 * list, the chevron only toggles the list.
 *   pink   - the selected row (the one that owns the active view)
 *   orange - a parent of the selected row, whether its list is open or closed
 * When the sidebar is collapsed only the top-level icons are shown (the label
 * moves to a tooltip); a group's open/closed state is kept for when it's
 * expanded again.
 */
function SidebarItem({ item, depth, tree }) {
  const { selectedId, pathIds, openIds, collapsed, disabledIds, onSelect, onToggle } = tree;
  const hasChildren = Boolean(item.children);
  const disabled = disabledIds.has(item.id);
  const isOpen = hasChildren && !collapsed && !disabled && openIds.has(item.id);
  const isSelected = item.id === selectedId;
  const isParentOfSelected = !isSelected && pathIds.has(item.id);
  const compact = depth >= 2 || Boolean(item.compact); // `compact` lets a long label opt in one level up
  const size = compact ? ROW_SIZE.compact : ROW_SIZE.regular;

  let tone;
  if (isSelected) tone = 'bg-pink-100 text-pink-700';
  else if (isParentOfSelected) tone = 'bg-orange-100 text-orange-700';
  else if (disabled) tone = `${size.idle} opacity-50`;
  else tone = `${size.idle} hover:bg-gray-100`;

  return (
    <div>
      <div
        style={{ marginLeft: indentFor(depth) }}
        className={`flex items-center transition-colors ${size.row} ${tone}`}
      >
        <button
          onClick={() => onSelect(item)}
          disabled={disabled}
          aria-current={isSelected ? 'page' : undefined}
          title={collapsed ? item.label : undefined}
          aria-label={collapsed ? item.label : undefined}
          className={`flex-1 min-w-0 flex items-center text-left disabled:cursor-not-allowed ${
            collapsed ? 'justify-center py-2' : `${size.label} ${hasChildren ? '' : size.endPad}`
          }`}
        >
          {compact ? (
            <>
              {depth >= 2 && <span aria-hidden="true">•</span>}
              <span className="text-base" aria-hidden="true">{item.icon}</span>
            </>
          ) : (
            <IconBadge key={item.iconUrl || ''} id={item.id} icon={item.icon} iconUrl={item.iconUrl} />
          )}
          {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
        </button>
        {hasChildren && !collapsed && !disabled && (
          <ChevronToggle label={item.label} isOpen={isOpen} onToggle={() => onToggle(item.id)} />
        )}
      </div>

      {isOpen && (
        <div className="mt-1 flex flex-col gap-1">
          {item.children.map((child) => (
            <SidebarItem key={child.id} item={child} depth={depth + 1} tree={tree} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Open/closed state of a course tree's groups (starts with the groups flagged
 * `defaultOpen`). Shared by the student sidebar and the admin lesson plan.
 */
export function useCourseTreeGroups(items) {
  const [openGroups, setOpenGroups] = useState(() => new Set(collectDefaultOpen(items)));

  const toggleGroup = (id) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const openGroup = useCallback((id) => setOpenGroups((prev) => new Set(prev).add(id)), []);

  return { openGroups, toggleGroup, openGroup };
}

/**
 * The course tree itself (English > Action Songs / Scenes ..., Computer Apps,
 * Stories ...): the student Learning Zone sidebar's nav, also reused by the
 * admin Create Lesson Plan page. `onSelect(item)` decides what a row does.
 */
// `disabledIds`: rows shown greyed out and not clickable (none on the admin lesson plan).
export function CourseTree({ items, activeView, openGroups, collapsed = false, disabledIds = new Set(), onSelect, onToggle }) {
  // Everything a SidebarItem needs: which row is selected, which rows lead to
  // it, which groups are open, and what to do on click / toggle.
  const activePath = findActivePath(items, activeView) || [];
  const tree = {
    selectedId: activePath[activePath.length - 1]?.id,
    pathIds: new Set(activePath.map((item) => item.id)),
    openIds: openGroups,
    collapsed,
    disabledIds,
    onSelect,
    onToggle,
  };

  return (
    <nav className="flex flex-col gap-1">
      {items.map((item) => (
        <SidebarItem key={item.id} item={item} depth={0} tree={tree} />
      ))}
    </nav>
  );
}

// `items` is SIDEBAR_TREE plus the courses' task type rows and the added subcategories
// (see courseSubcategoryRows.js).
export default function LearningSidebar({ items = SIDEBAR_TREE, activeView, onSelectView }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const sessionTime = useSessionTime();
  const { openGroups, toggleGroup, openGroup } = useCourseTreeGroups(items);
  const [collapsed, setCollapsed] = useState(false);

  const displayName = user?.name || 'Student';
  const initial = displayName.charAt(0).toUpperCase();

  // The row the page opens on can sit in a closed group (one picked in the Shop's sidebar, which goes
  // back to the Learning Zone with it): open the groups leading to it, once, as soon as it is in the
  // tree (course rows load after the page opens).
  const activeRowRevealed = useRef(false);
  useEffect(() => {
    if (activeRowRevealed.current) return;
    const path = findActivePath(items, activeView);
    if (!path) return;
    activeRowRevealed.current = true;
    path.forEach((item) => item.children && openGroup(item.id));
  }, [items, activeView, openGroup]);

  // Selecting a row opens it when it's a group, then acts on it: switch the
  // view, navigate away, or (no destination yet) say "Coming soon!".
  const handleSelect = (item) => {
    if (item.children) openGroup(item.id);
    if (item.view) onSelectView(item.view);
    else if (item.to) navigate(item.to);
    else if (!item.children) toast('Coming soon!', { icon: '🚧' });
  };

  return (
    <aside
      className={`${
        collapsed ? 'w-20 p-2' : 'w-64 p-4'
      } shrink-0 bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col gap-4 h-fit lg:h-auto lg:overflow-x-hidden transition-[width,padding] duration-200 ${LZ_SCROLL_REGION}`}
    >
      {/* Expand / collapse */}
      <div className={`flex ${collapsed ? 'justify-center' : 'justify-end'}`}>
        <button
          type="button"
          onClick={() => setCollapsed((prev) => !prev)}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          className="w-7 h-7 rounded-full border border-gray-200 bg-white text-indigo-500 flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors"
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </div>

      {/* Profile */}
      <div className={`flex flex-col items-center text-center pb-1 ${collapsed ? '' : 'pt-2'}`}>
        <div
          title={collapsed ? displayName : undefined}
          className={`${
            collapsed ? 'h-12 w-12 text-xl' : 'h-24 w-24 text-4xl'
          } rounded-full bg-gradient-to-br from-orange-300 to-pink-400 text-white flex items-center justify-center font-extrabold shadow-md ring-4 ring-white outline outline-1 outline-gray-100 overflow-hidden`}
        >
          {initial}
        </div>
        {collapsed ? (
          <div
            title="Session Timer"
            className="mt-2 bg-green-50 text-green-700 border-2 border-green-200 rounded-full px-2 py-0.5"
          >
            <span className="font-extrabold text-xs tabular-nums">{formatTime(sessionTime).replace(/ /g, '')}</span>
          </div>
        ) : (
          <>
            <p className="mt-3 text-xl font-extrabold text-gray-900">{displayName}</p>
            <div className="mt-2 flex items-center gap-2 bg-green-50 text-green-700 border-2 border-green-200 rounded-full px-5 py-1.5">
              <span className="text-base">⏱️</span>
              <span className="font-extrabold text-lg tabular-nums">{formatTime(sessionTime)}</span>
            </div>
            <p className="text-xs font-semibold text-gray-400 mt-1">Session Timer</p>
          </>
        )}
      </div>

      {/* Nav tree */}
      <CourseTree
        items={items}
        activeView={activeView}
        openGroups={openGroups}
        collapsed={collapsed}
        disabledIds={DISABLED_ROWS}
        onSelect={handleSelect}
        onToggle={toggleGroup}
      />

      {/* Logout - last item; mt-auto pins it to the bottom when the sidebar is taller than its content */}
      <div className="mt-auto pt-3 border-t border-gray-100">
        <button
          type="button"
          onClick={logout}
          title={collapsed ? 'Logout' : undefined}
          aria-label="Logout"
          className={`w-full flex items-center rounded-xl text-[15px] font-bold text-red-600 bg-red-50 hover:bg-red-100 transition-colors py-2 ${
            collapsed ? 'justify-center' : 'gap-3 pl-2 pr-2'
          }`}
        >
          <span className="w-8 h-8 shrink-0 rounded-full bg-red-100 flex items-center justify-center text-base" aria-hidden="true">
            🚪
          </span>
          {!collapsed && <span className="flex-1 text-left">Logout</span>}
        </button>
      </div>
    </aside>
  );
}
